/**
 * Scores module — startPlay, submitScore, getLeaderboard.
 *
 * Writes go through Edge Functions (anti-cheat enforcement). Reads go
 * through the Supabase client to the `get_leaderboard` /
 * `get_leaderboard_rank` RPCs (migration 0077), which run as the caller:
 * RLS and the column grants on `scores` apply exactly as a direct read.
 */
import type {
  ScoreEntry,
  SubmitScoreOptions,
  SubmitScoreResult,
  LeaderboardOptions,
} from './types';
import { VibecadeError, VibecadeErrorCode } from './types';
import type { SdkContext } from './internal/context';
import { invokeEdge } from './internal/edgeFunctions';
import { wrapError } from './internal/errors';
import { postToHost } from './internal/host';
import { withReadTimeout } from './internal/readTimeout';

interface GameMeta {
  scoreHigherIsBetter: boolean;
  requiresPlayToken: boolean;
}

/**
 * The RPC is missing: 42883 (no such function), PGRST202 (PostgREST's own
 * "could not find the function"), PGRST203 (an ambiguous overload). The
 * same set the platform's own Edge Functions key their pre-migration
 * handling on. See `getLeaderboard` for what the SDK does with it.
 */
const MISSING_RPC_CODES = new Set(['42883', 'PGRST202', 'PGRST203']);

/** A direct `scores` read, as the pre-0077 fallback selects it. */
interface RawScoreRow {
  id: number;
  score: number;
  created_at: string;
  player_id: string;
}

interface RawProfile {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

/** One row of `get_leaderboard` (migration 0077): the board row plus the
 *  player's public profile, joined server-side over the page only. */
interface RawBoardRow {
  rank: number;
  id: number;
  score: number;
  created_at: string;
  player_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export class ScoresModule {
  private gameMeta: GameMeta | null = null;
  // Lazy-discovered. null = unknown, true/false = learned from the games
  // catalog (ensureGameMeta) or from a successful / auto-recovered
  // submit. Lets us fetch a play token up front instead of relying on
  // the catch path (which costs a deliberate 400 per game load).
  private requiresPlayToken: boolean | null = null;

  constructor(private readonly ctx: SdkContext) {}

  async startPlay(): Promise<{ playToken: string }> {
    return invokeEdge<{ playToken: string }>(this.ctx, 'start-play', {
      gameId: this.ctx.config.gameId,
      apiKey: this.ctx.config.apiKey,
    });
  }

  /**
   * Submit a score. Auto-handles play tokens: if the game requires one
   * (`games.requires_play_token = true`) and the caller didn't supply
   * `opts.playToken`, the SDK transparently calls `startPlay()` and
   * retries. Game devs no longer need to know the play-token concept
   * exists unless they want explicit control.
   *
   * Tokens are single-use, so the SDK fetches a fresh one for every
   * submit on token-required games (it doesn't try to reuse). Costs
   * one extra round-trip per submit on those games — same as the
   * manual flow.
   */
  async submitScore(opts: SubmitScoreOptions): Promise<SubmitScoreResult> {
    if (!opts || typeof opts.score !== 'number' || !Number.isFinite(opts.score)) {
      throw new VibecadeError(
        VibecadeErrorCode.InvalidBody,
        'submitScore: score must be a finite number',
      );
    }

    const submit = (token?: string): Promise<SubmitScoreResult> => {
      const body: Record<string, unknown> = {
        gameId: this.ctx.config.gameId,
        apiKey: this.ctx.config.apiKey,
        score: opts.score,
      };
      if (token) body.playToken = token;
      if (opts.meta) body.meta = opts.meta;
      if (opts.boardKey !== undefined) body.boardKey = opts.boardKey;
      return invokeEdge<SubmitScoreResult>(this.ctx, 'submit-score', body);
    };

    // Learn whether this game requires a play token BEFORE submitting,
    // so token-required games fetch a token up front rather than
    // discovering the requirement via a deliberate 400 (the catch-retry
    // fallback below). Best-effort: if the catalog lookup fails for any
    // reason, fall through to the speculative path, which still recovers.
    let token = opts.playToken;
    if (!token && this.requiresPlayToken === null) {
      try {
        await this.ensureGameMeta();
      } catch {
        /* fall back to the speculative-submit-and-retry path below */
      }
    }
    // Pre-fetch a token when we know one is required. Skips the
    // speculative-fail-and-retry path entirely.
    if (!token && this.requiresPlayToken === true) {
      token = (await this.startPlay()).playToken;
    }

    let result: SubmitScoreResult;
    try {
      result = await submit(token);
      // First successful submit teaches us the requirement state.
      if (this.requiresPlayToken === null) {
        this.requiresPlayToken = !!token;
      }
    } catch (e) {
      // Auto-recover from "this game needs a play token" on the first
      // submit. Fetch a token, retry once. We only do this when the
      // caller didn't provide a token themselves — if they passed an
      // explicit token that was rejected, that's a real error to
      // surface, not a silent retry.
      if (
        !token &&
        e instanceof VibecadeError &&
        e.code === VibecadeErrorCode.PlayTokenRequired
      ) {
        this.requiresPlayToken = true;
        const fresh = (await this.startPlay()).playToken;
        result = await submit(fresh);
      } else {
        throw e;
      }
    }

    // Notify the embedding launcher (if any) so it can toast the
    // submission. No-op when running standalone or when the consumer
    // disabled auto-host messages (config.autoHostMessages = false).
    postToHost(
      opts.boardKey === undefined
        ? { type: 'score_submitted', rank: result.rank, source: 'vibecade-sdk' }
        : { type: 'score_submitted', rank: result.rank, boardKey: opts.boardKey,
            source: 'vibecade-sdk' },
      this.ctx.config.autoHostMessages,
    );
    return result;
  }

  /**
   * One page of the board, ONE ROW PER PLAYER.
   *
   * The rows come from the platform's `get_leaderboard` RPC (migration
   * 0077), which applies the rule in the database: a player appears once,
   * at their best score in scope, ranked against other players' bests.
   * Before 0077 this read `scores` rows straight back, so five finishes by
   * one player held five slots and cost everyone below them five ranks.
   * The rule lives server-side rather than here because the launcher and
   * the public pack embed read the same boards, and a client-side filter
   * would have left the three disagreeing about who is second.
   *
   * `rank` is computed by the RPC as offset + position, and the around-me
   * offset comes from `get_leaderboard_rank` under the same rule, so the
   * numbers on a centred page are the numbers a top-N page would show.
   *
   * Score direction (`games.score_higher_is_better`) is read by the RPC,
   * not translated here: an ORDER BY that depended on it could not use the
   * board index, which is the 0052 lesson the migration restates.
   */
  async getLeaderboard(opts: LeaderboardOptions = {}): Promise<ScoreEntry[]> {
    const limit = Math.max(1, Math.min(100, opts.limit ?? 20));
    const scope = opts.scope ?? 'all-time';
    const since = windowStart(scope);
    // Omitted means every board, which is what an unsegmented game wants and
    // already had. Only an explicit key narrows the read. Sent as null rather
    // than dropped so the RPC's default cannot drift away from this contract.
    const boardKey = opts.boardKey ?? null;

    try {
      const offset = opts.around === 'me' ? await this.aroundMeOffset(limit, boardKey, since) : 0;
      if (offset === null) return this.legacyLeaderboard(opts, limit, since);
      const { data, error } = await withReadTimeout('leaderboard read', (signal) =>
        this.ctx.db
          .rpc('get_leaderboard', {
            p_game_id: this.ctx.config.gameId,
            p_board_key: boardKey,
            p_unkeyed_only: false,
            p_since: since,
            p_limit: limit,
            p_offset: offset,
          })
          .abortSignal(signal),
      );
      if (error) {
        // DEPLOY WINDOW. The CDN build of this SDK (sdk.vibecade.ai/v1/,
        // which embedded games load by floating URL) and the launcher
        // deploy automatically when the platform's main branch moves, and
        // migrations are applied by an operator afterwards. Between the
        // two, the RPC does not exist. A board that errored for every game
        // in that window would be the platform's outage, not the game's,
        // so the read falls back to what it did before 0077, one row per
        // SUBMISSION, until the migration lands, and says so on the debug
        // log. Only the missing-function codes take this path: any other
        // error is still the failure it always was.
        if (MISSING_RPC_CODES.has(error.code ?? '')) {
          return this.legacyLeaderboard(opts, limit, since);
        }
        throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, error.message);
      }
      return ((data ?? []) as RawBoardRow[]).map((r) => this.toEntry(r));
    } catch (e) {
      throw wrapError(e, VibecadeErrorCode.LeaderboardFailed, 'Could not load leaderboard');
    }
  }

  // -------------------------------------------------------------------------
  // private
  // -------------------------------------------------------------------------

  /**
   * Where a page centred on the current player starts. The player's rank
   * under the one-row-per-player rule, from `get_leaderboard_rank`, less
   * half a page, floored at the top. A player with no score in scope has no
   * rank (null) and gets the top of the board, which is the documented
   * fallback.
   *
   * The board key and the window are the same ones the page is read with.
   * Before 0077 this was the third of three queries and the one that failed
   * silently when it lost the key: it counted better scores across the whole
   * GAME while the rows came from one board, paging a board the player led
   * past its own end. One argument list shared with the page read closes
   * that.
   */
  private async aroundMeOffset(
    limit: number,
    boardKey: string | null,
    since: string | null,
  ): Promise<number | null> {
    const playerId = this.ctx.player.current?.id;
    if (!playerId) {
      throw new VibecadeError(
        VibecadeErrorCode.NotInitialized,
        'getLeaderboard({ around: "me" }) requires a player — call init() first',
      );
    }
    const { data, error } = await withReadTimeout('leaderboard rank read', (signal) =>
      this.ctx.db
        .rpc('get_leaderboard_rank', {
          p_game_id: this.ctx.config.gameId,
          p_player_id: playerId,
          p_board_key: boardKey,
          p_unkeyed_only: false,
          p_since: since,
        })
        .abortSignal(signal),
    );
    if (error) {
      // Null means "no RPC": the caller takes the deploy-window fallback.
      // See getLeaderboard.
      if (MISSING_RPC_CODES.has(error.code ?? '')) return null;
      throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, error.message);
    }
    const myRank = typeof data === 'number' ? data : null;
    if (myRank === null) return 0;
    return Math.max(0, myRank - 1 - Math.floor(limit / 2));
  }

  // -------------------------------------------------------------------------
  // The pre-0077 read, kept as the deploy-window fallback (see getLeaderboard).
  // Verbatim the shape it had: three direct PostgREST reads, one row per
  // submission, rank counted here. Delete once 0077 has been applied to
  // every environment this SDK build can reach.
  // -------------------------------------------------------------------------

  private async legacyLeaderboard(
    opts: LeaderboardOptions,
    limit: number,
    since: string | null,
  ): Promise<ScoreEntry[]> {
    this.ctx.logger.debug(
      'getLeaderboard: platform migration 0077 not applied yet; reading one row per submission',
    );
    const { ascending } = await this.getOrdering();
    const base = this.legacyBaseQuery(since, ascending, opts.boardKey);
    const { rows, startRank } =
      opts.around === 'me'
        ? await this.legacyAroundMe(base, limit, ascending, since, opts.boardKey)
        : await this.legacyTop(base, limit);
    if (rows.length === 0) return [];
    const profiles = await this.legacyProfiles(rows.map((r) => r.player_id));
    return rows.map((r, i) => {
      const profile = profiles.get(r.player_id);
      return this.toEntry({
        rank: startRank + i,
        id: r.id,
        score: r.score,
        created_at: r.created_at,
        player_id: r.player_id,
        display_name: profile?.display_name ?? null,
        avatar_url: profile?.avatar_url ?? null,
      });
    });
  }

  private legacyBaseQuery(since: string | null, ascending: boolean, boardKey?: string) {
    // `meta` deliberately omitted: migration 0020 hides scores.meta behind
    // a column-level grant.
    let q = this.ctx.db
      .from('scores')
      .select('id, score, created_at, player_id')
      .eq('game_id', this.ctx.config.gameId)
      .order('score', { ascending })
      .order('created_at', { ascending: true });
    if (since) q = q.gte('created_at', since);
    if (boardKey !== undefined) q = q.eq('board_key', boardKey);
    return q;
  }

  private async legacyTop(
    query: ReturnType<ScoresModule['legacyBaseQuery']>,
    limit: number,
  ): Promise<{ rows: RawScoreRow[]; startRank: number }> {
    const { data, error } = await withReadTimeout('leaderboard read', (signal) =>
      query.abortSignal(signal).limit(limit),
    );
    if (error) throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, error.message);
    return { rows: (data ?? []) as RawScoreRow[], startRank: 1 };
  }

  private async legacyAroundMe(
    query: ReturnType<ScoresModule['legacyBaseQuery']>,
    limit: number,
    ascending: boolean,
    since: string | null,
    boardKey?: string,
  ): Promise<{ rows: RawScoreRow[]; startRank: number }> {
    // aroundMeOffset already refused a missing player before we got here.
    const playerId = this.ctx.player.current?.id as string;
    let bestQ = this.ctx.db
      .from('scores')
      .select('score')
      .eq('game_id', this.ctx.config.gameId)
      .eq('player_id', playerId)
      .order('score', { ascending })
      .limit(1);
    if (since) bestQ = bestQ.gte('created_at', since);
    if (boardKey !== undefined) bestQ = bestQ.eq('board_key', boardKey);
    const { data: best, error: bestErr } = await withReadTimeout('leaderboard read', (signal) =>
      bestQ.abortSignal(signal).maybeSingle(),
    );
    if (bestErr) throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, bestErr.message);
    if (!best) return this.legacyTop(query, limit);

    let rankQ = this.ctx.db
      .from('scores')
      .select('id', { count: 'exact', head: true })
      .eq('game_id', this.ctx.config.gameId);
    rankQ = ascending ? rankQ.lt('score', best.score) : rankQ.gt('score', best.score);
    if (since) rankQ = rankQ.gte('created_at', since);
    if (boardKey !== undefined) rankQ = rankQ.eq('board_key', boardKey);
    const { count, error: cntErr } = await withReadTimeout('leaderboard rank read', (signal) =>
      rankQ.abortSignal(signal),
    );
    if (cntErr) throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, cntErr.message);
    const myRank = (count ?? 0) + 1;
    const startRank = Math.max(1, myRank - Math.floor(limit / 2));
    const startOffset = startRank - 1;
    const { data, error } = await withReadTimeout('leaderboard read', (signal) =>
      query.abortSignal(signal).range(startOffset, startOffset + limit - 1),
    );
    if (error) throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, error.message);
    return { rows: (data ?? []) as RawScoreRow[], startRank };
  }

  private async legacyProfiles(playerIds: string[]): Promise<Map<string, RawProfile>> {
    const unique = [...new Set(playerIds)];
    if (unique.length === 0) return new Map();
    const { data, error } = await withReadTimeout('player profiles read', (signal) =>
      this.ctx.db
        .from('public_player_profiles')
        .select('id, display_name, avatar_url')
        .abortSignal(signal)
        .in('id', unique),
    );
    if (error) throw new VibecadeError(VibecadeErrorCode.LeaderboardFailed, error.message);
    return new Map(((data ?? []) as RawProfile[]).map((p) => [p.id, p]));
  }

  private async getOrdering(): Promise<{ ascending: boolean }> {
    const meta = await this.ensureGameMeta();
    return { ascending: !meta.scoreHigherIsBetter };
  }

  private toEntry(row: RawBoardRow): ScoreEntry {
    return {
      id: row.id,
      score: row.score,
      createdAt: row.created_at,
      // `meta` always null for public reads: migration 0020 hides
      // scores.meta behind a column-level grant, and the RPC runs as the
      // caller (SECURITY INVOKER) so it could not return it if it tried.
      // Field retained on ScoreEntry for backward compatibility with game
      // integrations that referenced it. See DECISIONS #96.
      meta: null,
      rank: row.rank,
      player: {
        id: row.player_id,
        displayName: row.display_name ?? 'Unknown Player',
        avatarUrl: row.avatar_url ?? null,
      },
    };
  }

  /**
   * Load + cache this game's catalog metadata (score ordering + play-token
   * requirement) in a single `games` read. Seeds `requiresPlayToken` from
   * the authoritative catalog row so `submitScore` can fetch a play token
   * up front instead of discovering the requirement via a throwaway 400.
   * `requires_play_token` is a public, RLS-readable column (migration
   * 0001); only `scores.meta` is column-locked (migration 0020).
   */
  private async ensureGameMeta(): Promise<GameMeta> {
    if (!this.gameMeta) {
      const { data, error } = await withReadTimeout('games catalog read', (signal) =>
        this.ctx.db
          .from('games')
          .select('score_higher_is_better, requires_play_token')
          .eq('id', this.ctx.config.gameId)
          .abortSignal(signal)
          .maybeSingle(),
      );
      if (error || !data) {
        throw new VibecadeError(
          VibecadeErrorCode.GameNotFound,
          'Game not found or not accessible',
        );
      }
      this.gameMeta = {
        scoreHigherIsBetter: data.score_higher_is_better as boolean,
        requiresPlayToken: data.requires_play_token as boolean,
      };
      // Seed the requirement from the catalog unless a prior submit
      // already learned it (don't clobber a learned value).
      if (this.requiresPlayToken === null) {
        this.requiresPlayToken = this.gameMeta.requiresPlayToken;
      }
    }
    return this.gameMeta;
  }
}

/** Returns ISO string for the start of the requested time window, or null. */
function windowStart(scope: LeaderboardOptions['scope']): string | null {
  const now = Date.now();
  switch (scope) {
    case 'today':
      return new Date(now - 24 * 60 * 60 * 1000).toISOString();
    case 'weekly':
      return new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
    case 'global':
    case 'all-time':
    case undefined:
      return null;
    default:
      return null;
  }
}
